import { useEffect, useState } from "react";
import { Inbox as InboxApi, type InboxItem } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Pagination } from "@/components/Pagination";
import { Trash2, Mail, MailOpen, CheckCheck, RefreshCw } from "lucide-react";
import { Link } from "@/lib/router";
import { confirmDialog } from "@/components/ui/dialog";

const KIND_LABEL: Record<string, { label: string; tone: "default" | "secondary" | "destructive" }> = {
  subscription:  { label: "订阅推送",     tone: "default" },
  vip_expiring:  { label: "VIP 到期提醒", tone: "secondary" },
  vip_expired:   { label: "VIP 已过期",   tone: "destructive" },
  low_points:    { label: "积分提醒",     tone: "secondary" },
  broadcast:     { label: "系统公告",     tone: "default" },
};

export function InboxTab() {
  const [items, setItems] = useState<InboxItem[]>([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [page, setPage] = useState(1);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const pageSize = 20;

  const reload = () => {
    setLoading(true);
    InboxApi.list(page, pageSize, unreadOnly)
      .then(r => { setItems(r.items); setTotal(r.total); setUnread(r.unread); })
      .finally(() => setLoading(false));
  };
  useEffect(reload, [page, unreadOnly]);

  const onRead = async (it: InboxItem) => {
    if (it.is_read) return;
    await InboxApi.markRead(it.id);
    setItems(items.map(x => x.id === it.id ? { ...x, is_read: true } : x));
    setUnread(Math.max(0, unread - 1));
  };
  const onReadAll = async () => {
    await InboxApi.markAllRead();
    reload();
  };
  const onDelete = async (it: InboxItem) => {
    if (!await confirmDialog({ title: "删除这条消息？", destructive: true, confirmText: "删除" })) return;
    await InboxApi.remove(it.id);
    reload();
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          站内信
          {unread > 0 && <Badge variant="destructive">{unread} 未读</Badge>}
        </CardTitle>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setUnreadOnly(!unreadOnly)}>
            {unreadOnly ? "查看全部" : "仅未读"}
          </Button>
          <Button size="sm" variant="outline" onClick={onReadAll} disabled={unread === 0}>
            <CheckCheck className="h-4 w-4 mr-1" /> 全部已读
          </Button>
          <Button size="sm" variant="ghost" onClick={reload}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-muted-foreground py-8 text-center">加载中...</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center">没有消息。</div>
        ) : (
          <div className="space-y-2">
            {items.map(it => {
              const meta = KIND_LABEL[it.kind] || { label: it.kind, tone: "secondary" as const };
              return (
                <div key={it.id}
                     className={`rounded-md border p-3 transition-colors ${it.is_read ? "bg-background" : "bg-primary/5 border-primary/30"}`}
                     onClick={() => onRead(it)}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {it.is_read
                        ? <MailOpen className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        : <Mail className="h-4 w-4 text-primary flex-shrink-0" />}
                      <Badge variant={meta.tone}>{meta.label}</Badge>
                      <span className={`text-sm truncate ${it.is_read ? "" : "font-semibold"}`}>{it.title}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-muted-foreground">{new Date(it.created_at).toLocaleString()}</span>
                      <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); onDelete(it); }}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {it.body && (
                    <div className="mt-2 text-sm text-muted-foreground whitespace-pre-wrap pl-6">
                      <InboxBody body={it.body} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {total > pageSize && (
          <div className="mt-4">
            <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** 解析 body 中的 /p/{slug} 为可点击链接（订阅类消息） */
function InboxBody({ body }: { body: string }) {
  const m = body.match(/\/p\/([\w\-]+)/);
  if (!m) return <>{body}</>;
  const before = body.slice(0, m.index!);
  const after = body.slice(m.index! + m[0].length);
  return (
    <>
      {before}
      <Link to={m[0]} className="text-primary hover:underline">{m[0]}</Link>
      {after}
    </>
  );
}
